/**
 * Shared UI helpers used across every feature view.
 *
 * Centralizes the small primitives that were previously copy-pasted into each
 * panel (HTML escaping, the one-shot entrance animation) and adds first-class
 * loading / error / empty states so views never fail silently again.
 *
 * RULE: never hard-code colors here or in any view — always reference the
 * `--c-*` CSS custom properties from style.css so light/dark themes stay intact.
 * @module core/ui
 */
import { apiFetch } from "../auth.ts";

const _rendered = new WeakSet<HTMLElement>();

/**
 * Drop-in replacement for `el.innerHTML = html` that plays the `.animate-in`
 * entrance exactly once per container (subsequent data refreshes don't replay).
 */
export function setHTML(el: HTMLElement, html: string): void {
	const first = !_rendered.has(el);
	if (first) {
		_rendered.add(el);
		el.classList.add("animate-in");
	}
	el.innerHTML = html;
	if (first) setTimeout(() => el.classList.remove("animate-in"), 700);
}

/** Escape untrusted text for safe interpolation into innerHTML. */
export function escapeHtml(text: string): string {
	const div = document.createElement("div");
	div.textContent = text ?? "";
	return div.innerHTML;
}

/** Escape a value for use inside a double-quoted HTML attribute. */
export function escapeAttr(text: string): string {
	return String(text ?? "")
		.replace(/&/g, "&amp;")
		.replace(/"/g, "&quot;")
		.replace(/</g, "&lt;")
		.replace(/>/g, "&gt;");
}

/** Create a detached element with optional class and innerHTML. */
export function el<K extends keyof HTMLElementTagNameMap>(
	tag: K,
	className?: string,
	html?: string,
): HTMLElementTagNameMap[K] {
	const node = document.createElement(tag);
	if (className) node.className = className;
	if (html !== undefined) node.innerHTML = html;
	return node;
}

// ── Async state renderers ────────────────────────────────────────────────────

/** Markup for an inline spinner with an optional label. */
export function spinnerHTML(label = "Loading…"): string {
	return `<div class="state-block state-loading">
    <span class="state-spinner" aria-hidden="true"></span>
    <span class="state-text">${escapeHtml(label)}</span>
  </div>`;
}

/** Render a loading state into a host element. */
export function renderLoading(host: HTMLElement, label?: string): void {
	host.innerHTML = spinnerHTML(label);
}

/** Render an empty state (no data) into a host element. */
export function renderEmpty(
	host: HTMLElement,
	message: string,
	icon?: string,
): void {
	host.innerHTML = `<div class="state-block state-empty">
    ${icon ? `<span class="state-icon" aria-hidden="true">${icon}</span>` : ""}
    <span class="state-text">${escapeHtml(message)}</span>
  </div>`;
}

/**
 * Render an error state into a host element. When `onRetry` is supplied a
 * "Retry" button is wired up so users can recover without reloading the page.
 */
export function renderError(
	host: HTMLElement,
	message: string,
	onRetry?: () => void,
): void {
	host.innerHTML = `<div class="state-block state-error" role="alert">
    <span class="state-icon" aria-hidden="true">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
        stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/></svg>
    </span>
    <span class="state-text">${escapeHtml(message)}</span>
    ${onRetry ? '<button type="button" class="state-retry">Retry</button>' : ""}
  </div>`;
	if (onRetry)
		host.querySelector(".state-retry")?.addEventListener("click", onRetry);
}

// ── REST helpers ─────────────────────────────────────────────────────────────

/** Error carrying the HTTP status of a failed API call. */
export class ApiError extends Error {
	constructor(
		message: string,
		readonly status: number,
	) {
		super(message);
		this.name = "ApiError";
	}
}

/**
 * Authenticated JSON fetch that throws a descriptive {@link ApiError} on any
 * non-2xx response (surfacing the server's `message`/`error` field when present).
 * Use this in views so failures reach a visible error state instead of a
 * swallowed `.catch(() => {})`.
 */
export async function apiJSON<T = unknown>(
	path: string,
	init?: RequestInit,
): Promise<T> {
	const res = await apiFetch(path, init);
	if (!res.ok) {
		let msg = `Request failed (HTTP ${res.status})`;
		try {
			const ct = res.headers.get("content-type") ?? "";
			if (ct.includes("application/json")) {
				const body = await res.json();
				msg = body?.message ?? body?.error ?? msg;
			} else {
				const text = (await res.text()).trim();
				if (text) msg = text;
			}
		} catch {
			/* keep generic message */
		}
		throw new ApiError(msg, res.status);
	}
	if (res.status === 204) return undefined as T;
	return (await res.json()) as T;
}

export function getPrefTimeFormat(): "12h" | "24h" {
	return (localStorage.getItem("pref-time-format") as "12h" | "24h") || "24h";
}

export function getPrefDateFormat():
	| "YYYY-MM-DD"
	| "MM/DD/YYYY"
	| "DD/MM/YYYY" {
	return (
		(localStorage.getItem("pref-date-format") as
			| "YYYY-MM-DD"
			| "MM/DD/YYYY"
			| "DD/MM/YYYY") || "YYYY-MM-DD"
	);
}

/** Format an ISO timestamp, number, or Date as a short localized time (HH:MM). */
export function fmtTime(
	val?: string | number | Date,
	includeSeconds = false,
): string {
	if (!val) return "—";
	try {
		const hour12 = getPrefTimeFormat() === "12h";
		return new Date(val).toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
			second: includeSeconds ? "2-digit" : undefined,
			hour12: hour12,
		});
	} catch {
		return String(val);
	}
}

/** Format an ISO timestamp, number, or Date as a localized date. */
export function fmtDate(val?: string | number | Date): string {
	if (!val) return "—";
	try {
		const d = new Date(val);
		const dateFormat = getPrefDateFormat();

		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, "0");
		const day = String(d.getDate()).padStart(2, "0");

		if (dateFormat === "MM/DD/YYYY") {
			return `${month}/${day}/${year}`;
		}
		if (dateFormat === "DD/MM/YYYY") {
			return `${day}/${month}/${year}`;
		}
		return `${year}-${month}-${day}`;
	} catch {
		return String(val);
	}
}

/** Format an ISO timestamp, number, or Date as a localized date + time. */
export function fmtDateTime(
	val?: string | number | Date,
	includeSeconds = false,
): string {
	if (!val) return "—";
	try {
		const d = new Date(val);
		const timeFormat = getPrefTimeFormat();

		const hour12 = timeFormat === "12h";
		const timeStr = d.toLocaleTimeString([], {
			hour: "2-digit",
			minute: "2-digit",
			second: includeSeconds ? "2-digit" : undefined,
			hour12: hour12,
		});

		const dateStr = fmtDate(d);
		return `${dateStr} ${timeStr}`;
	} catch {
		return String(val);
	}
}
